// "Image N" labels: an image attached to a post is named in its text where it
// was pasted, dropped or picked ("[image 1]"), so that with several images the
// text can say which one it is about (as a CLI's "[Image #1]").
//
// - N counts the post's attached images only, in the order they were added;
//   the files are sent in that order, so the number does not change once sent.
// - In the box the label is text the box draws as a pill
//   (`image-label-layer.tsx`). Sent, it becomes the link to the file
//   (`[image 1](trameria://file/<id>)`, the syntax of a file in a document):
//   the agent is told which attachment it is, and anything drawing Markdown
//   shows a file named "image 1". Nothing new for the server to know; a
//   message draws the link to one of its own images as the same pill
//   (`attachments.tsx`), numbered again from its files.
// - Taking an image off takes its label out of the text and renumbers the
//   later ones.
import { isImageFileMime } from "@trameria/core/files-shared";
import { m } from "@trameria/i18n";

/** The name of the n-th image (from 1). */
export const imageName = (n: number): string => m.agents_image_n({ n });

/** The label as written in the box. */
export const imageLabel = (n: number): string => `[${imageName(n)}]`;

/**
 * Counted and shown as an image: drawn by the browser (not SVG, never served
 * inline). The box, the files under a message and the numbers all use this.
 */
export const isImage = (mime: string): boolean => isImageFileMime(mime);

const literal = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** A label in the text not already made a link: its number in group 1. */
function labelPattern(): RegExp {
  const [before, after] = imageName(0).split("0");
  return new RegExp(
    `\\[${literal(before ?? "")}(\\d+)${literal(after ?? "")}\\](?!\\()`,
    "g",
  );
}

/**
 * Puts the labels of `count` new images at the caret (after `from` images
 * already attached), one space apart from what is around them. Returns the
 * text and where the caret goes after them.
 */
export function insertImageLabels(
  text: string,
  caret: number,
  from: number,
  count: number,
): { text: string; caret: number } {
  if (count <= 0) return { text, caret };
  const labels = Array.from({ length: count }, (_, i) =>
    imageLabel(from + i + 1),
  ).join(" ");
  const before = text.slice(0, caret);
  const after = text.slice(caret);
  const lead = before === "" || /\s$/.test(before) ? "" : " ";
  const tail = after === "" || /^\s/.test(after) ? "" : " ";
  const put = `${lead}${labels}${tail}`;
  return {
    text: `${before}${put}${after}`,
    caret: before.length + lead.length + labels.length,
  };
}

/** Where the labels are in the text (`end` is past the closing bracket). */
export function labelRanges(
  text: string,
): { start: number; end: number; n: number }[] {
  return [...text.matchAll(labelPattern())].map((hit) => ({
    start: hit.index,
    end: hit.index + hit[0].length,
    n: Number(hit[1]),
  }));
}

/**
 * The text after the n-th image is taken off: its label goes (with one space
 * beside it), and the labels of later images count one less.
 */
export function dropImageLabel(text: string, n: number): string {
  const own = new RegExp(` ?${literal(imageLabel(n))}(?!\\()`, "g");
  return text
    .replace(own, "")
    .replace(labelPattern(), (whole, k: string) =>
      Number(k) > n ? imageLabel(Number(k) - 1) : whole,
    );
}

/** The text with every label counting `by` more (images put before them). */
export function shiftImageLabels(text: string, by: number): string {
  if (by === 0) return text;
  return text.replace(labelPattern(), (_whole, k: string) =>
    imageLabel(Number(k) + by),
  );
}

/**
 * Back from a sent text to the box's (a post taken back): each link to one of
 * `files` becomes the label of its place among their images. A link to
 * another file stays as written.
 */
export function unlinkImageLabels(
  text: string,
  files: readonly { id: string; mime: string }[],
): string {
  const images = files
    .filter((f) => isImage(f.mime))
    .map((f) => f.id.toLowerCase());
  const [before, after] = imageName(0).split("0");
  const link = new RegExp(
    `\\[${literal(before ?? "")}\\d+${literal(after ?? "")}\\]\\(trameria://file/([0-9a-fA-F-]+)\\)`,
    "g",
  );
  return text.replace(link, (whole, id: string) => {
    const n = images.indexOf(id.toLowerCase());
    return n < 0 ? whole : imageLabel(n + 1);
  });
}

/**
 * The text to send: each label of an attached image becomes the link to its
 * file. `files` are the box's attachments in the order they were added (the
 * numbers count them all); a label whose image is not up stays as written.
 */
export function linkImageLabels(
  text: string,
  files: readonly { fileId: string | null; mime: string }[],
): string {
  const images = files.filter((f) => isImage(f.mime));
  return text.replace(labelPattern(), (whole, k: string) => {
    const id = images[Number(k) - 1]?.fileId;
    return id
      ? `[${imageName(Number(k))}](trameria://file/${id.toLowerCase()})`
      : whole;
  });
}
